import { useEffect, useState } from 'react';

import useAiLoadAdvisory from 'hooks/useAiLoadAdvisory';
import type { AiLoadRateUnit, AiLoadRequest } from 'hooks/useAiLoadAdvisory/types';
import useCompatibilityAdvisory from 'hooks/useCompatibilityAdvisory';
import { DEFAULT_LABEL_MINIMUM_LB_PER_100_GAL } from 'hooks/useCompatibilityAdvisory/constants';
import { DEFAULT_WATER_PROFILE } from 'hooks/useCompatibilityAdvisory/mockData';
import type { CompatibilityRequest } from 'hooks/useCompatibilityAdvisory/types';
import useMixOrderAdvisory from 'hooks/useMixOrderAdvisory';
import type { CarrierType, MixOrderRequest } from 'hooks/useMixOrderAdvisory/types';

import {
    CARRIER_TYPE_FROM_LABEL,
    COMPATIBILITY_AGENT_KEYWORDS,
    DENSITY_REFERENCE_TEMP_F,
    DRY_FORMULATION_CODES,
    LIQUID_FORMULATION_CODES,
    MICROBIAL_KEYWORDS
} from './constants';
import type {
    AiLoadRow,
    AmsRecommendation,
    InteractionFinding,
    MixOrderIssue,
    MixRecipe,
    MixRecipeProduct,
    OrderedChemical,
    PhFinding,
    UseAdvisoryDataParams,
    UseAdvisoryDataReturn
} from './types';

// drType comes from the formulation code only. physicalState is sent raw so the
// backend can place an unknown-formulation product AND emit FORMULATION_UNKNOWN;
// deriving drType from it here would mark the position as evidence-based.
const detectDrType = (formulationCode: string | null | undefined): 'DRY' | 'LIQUID' | null => {
    const code = formulationCode?.toUpperCase();

    if (code && DRY_FORMULATION_CODES.has(code)) {
        return 'DRY';
    }

    if (code && LIQUID_FORMULATION_CODES.has(code)) {
        return 'LIQUID';
    }

    return null;
};

const detectIsCompatibilityAgent = (product: MixRecipeProduct): boolean => {
    const name = product.productName.toLowerCase();

    return COMPATIBILITY_AGENT_KEYWORDS.some(keyword => name.includes(keyword));
};

const detectIsMicrobial = (product: MixRecipeProduct): boolean => {
    const name = product.productName.toLowerCase();

    if (MICROBIAL_KEYWORDS.some(keyword => name.includes(keyword))) {
        return true;
    }

    return (product.classifications ?? []).some(classification =>
        MICROBIAL_KEYWORDS.some(keyword => classification.name.toLowerCase().includes(keyword))
    );
};

const buildMixOrderRequest = (recipe: MixRecipe, carrier: CarrierType): MixOrderRequest => ({
    carrier,
    chemicals: recipe.products.map(product => ({
        chemicalId: product.productId,
        drType: detectDrType(product.formulationCode),
        formulationCode: product.formulationCode ?? null,
        ingredients: (product.ingredients ?? []).map(ingredient => ({
            acidEquivalentPercentage: ingredient.acidEquivalentPercentage ?? null,
            casNumber: ingredient.casNumber,
            name: ingredient.name,
            percentage: ingredient.percentage
        })),
        isCompatibilityAgent: detectIsCompatibilityAgent(product),
        isMicrobial: detectIsMicrobial(product),
        labelName: product.productName,
        labelOverrideOrder: null,
        // Enrichment-curated hint only; the backend maps formulation codes itself.
        mixOrderGroup: product.mixOrderGroup ?? null,
        physicalState: product.physicalState ?? null
    }))
});

// Compat endpoint's Zod schema requires each product to have at least one
// ingredient. Custom products without ingredients (user-created with no
// active-ingredient data) would otherwise 400 the whole request and blank
// the panel. Skip them instead.
const buildCompatibilityRequest = (recipe: MixRecipe): CompatibilityRequest | null => {
    const products = recipe.products
        .filter(product => (product.ingredients ?? []).length > 0)
        .map(product => ({
            chemicalId: product.productId,
            ingredients: (product.ingredients ?? []).map(ingredient => ({
                acidEquivalentPercentage: ingredient.acidEquivalentPercentage ?? null,
                casNumber: ingredient.casNumber,
                name: ingredient.name,
                percentage: ingredient.percentage
            })),
            labelName: product.productName
        }));

    if (products.length === 0) {
        return null;
    }

    return {
        labelMinimumLbPer100Gallons: DEFAULT_LABEL_MINIMUM_LB_PER_100_GAL,
        products,
        waterProfile: DEFAULT_WATER_PROFILE
    };
};

// Maps SVM recipe rateUnit to the AI-load wire rateUnit. Identity on the
// shared five supported units; returns null if the product's rateUnit is not
// one the backend accepts (e.g., metric, custom). Callers skip products that
// map to null so a single unsupported unit does not 400 the whole request.
const SUPPORTED_AI_LOAD_RATE_UNITS: Record<string, AiLoadRateUnit> = {
    'gal/ac': 'gal/ac',
    'lb/ac': 'lb/ac',
    'oz/ac': 'oz/ac',
    'pt/ac': 'pt/ac',
    'qt/ac': 'qt/ac'
};

const toAiLoadRateUnit = (rateUnit: string): AiLoadRateUnit | null =>
    SUPPORTED_AI_LOAD_RATE_UNITS[rateUnit] ?? null;

const buildAiLoadRequest = (recipe: MixRecipe): AiLoadRequest | null => {
    const chemicals = recipe.products
        .map(product => {
            const mappedUnit = toAiLoadRateUnit(product.rateUnit);

            if (mappedUnit === null || !Number.isFinite(product.rate)) {
                return null;
            }

            return {
                chemicalId: product.productId,
                density:
                    product.densityLbPerGal !== null && product.densityLbPerGal !== undefined
                        ? {
                              lbPerGal: product.densityLbPerGal,
                              referenceTempF: DENSITY_REFERENCE_TEMP_F
                          }
                        : null,
                ingredients: (product.ingredients ?? []).map(ingredient => ({
                    acidEquivalentPercentage: ingredient.acidEquivalentPercentage ?? null,
                    casNumber: ingredient.casNumber,
                    name: ingredient.name,
                    percentage: ingredient.percentage
                })),
                labelName: product.productName,
                rate: product.rate,
                rateUnit: mappedUnit
            };
        })
        .filter((chemical): chemical is NonNullable<typeof chemical> => chemical !== null);

    if (chemicals.length === 0) {
        return null;
    }

    return { chemicals };
};

const useAdvisoryData = ({
    carrierType,
    mixRecipe
}: UseAdvisoryDataParams): UseAdvisoryDataReturn => {
    const [aiLoadRows, setAiLoadRows] = useState<AiLoadRow[]>([]);
    const [aiLoadError, setAiLoadError] = useState(false);
    const { fetch: fetchAiLoad } = useAiLoadAdvisory();

    const [mixOrderedChemicals, setMixOrderedChemicals] = useState<OrderedChemical[]>([]);
    const [mixOrderIssues, setMixOrderIssues] = useState<MixOrderIssue[]>([]);
    const [mixOrderError, setMixOrderError] = useState(false);
    const { fetch: fetchMixOrder, isLoading: isMixOrderLoading } = useMixOrderAdvisory();

    const [compatInteractions, setCompatInteractions] = useState<InteractionFinding[]>([]);
    const [compatPhWarnings, setCompatPhWarnings] = useState<PhFinding[]>([]);
    const [compatAmsRecommendation, setCompatAmsRecommendation] =
        useState<AmsRecommendation | null>(null);
    const [compatError, setCompatError] = useState(false);
    const { fetch: fetchCompatibility, isLoading: isCompatLoading } = useCompatibilityAdvisory();

    useEffect(() => {
        if (!mixRecipe || mixRecipe.products.length === 0) {
            setAiLoadRows([]);
            setAiLoadError(false);

            return;
        }

        let cancelled = false;

        setAiLoadError(false);

        (async () => {
            const request = buildAiLoadRequest(mixRecipe);

            if (request === null) {
                if (!cancelled) {
                    setAiLoadRows([]);
                }

                return;
            }

            const response = await fetchAiLoad({ request });

            if (cancelled) {
                return;
            }

            if (response) {
                setAiLoadRows(response.rows);
                setAiLoadError(false);
            } else {
                console.error('AI load request failed — panel may be stale');
                setAiLoadError(true);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [mixRecipe, fetchAiLoad]);

    useEffect(() => {
        if (!mixRecipe || mixRecipe.products.length === 0) {
            setMixOrderedChemicals([]);
            setMixOrderIssues([]);
            setMixOrderError(false);

            return;
        }

        let cancelled = false;
        const carrier = CARRIER_TYPE_FROM_LABEL[carrierType] ?? 'WATER';

        setMixOrderError(false);

        (async () => {
            const request = buildMixOrderRequest(mixRecipe, carrier);
            const response = await fetchMixOrder({ request });

            if (cancelled) {
                return;
            }

            if (response) {
                setMixOrderedChemicals(response.orderedChemicals);
                setMixOrderIssues(response.issues);
                setMixOrderError(false);
            } else {
                console.error('Mix-order request failed — panel may be stale');
                setMixOrderError(true);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [mixRecipe, carrierType, fetchMixOrder]);

    useEffect(() => {
        if (!mixRecipe || mixRecipe.products.length === 0) {
            setCompatInteractions([]);
            setCompatPhWarnings([]);
            setCompatAmsRecommendation(null);
            setCompatError(false);

            return;
        }

        let cancelled = false;

        setCompatError(false);

        (async () => {
            const request = buildCompatibilityRequest(mixRecipe);

            if (request === null) {
                if (!cancelled) {
                    setCompatInteractions([]);
                    setCompatPhWarnings([]);
                    setCompatAmsRecommendation(null);
                }

                return;
            }

            const response = await fetchCompatibility({ request });

            if (cancelled) {
                return;
            }

            if (response) {
                setCompatInteractions(response.interactions);
                setCompatPhWarnings(response.phWarnings);
                setCompatAmsRecommendation(response.amsRecommendation);
                setCompatError(false);
            } else {
                console.error('Compatibility-advisory request failed — panel may be stale');
                setCompatError(true);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [mixRecipe, fetchCompatibility]);

    return {
        aiLoadError,
        aiLoadRows,
        compatAmsRecommendation,
        compatError,
        compatInteractions,
        compatPhWarnings,
        isCompatLoading,
        isMixOrderLoading,
        mixOrderError,
        mixOrderIssues,
        mixOrderedChemicals
    };
};

export default useAdvisoryData;
